"use client";
import { useEffect, useState, useCallback, useRef, useMemo, memo } from "react";
import Image from "next/image";
import { apiService } from "@/services";
import { message } from "antd";
import { SelectField } from "@/components/UI/select-field";
import { InputField } from "@/components/UI/input-field";
import { ProfileData, UserData } from "@/app/types";
import { usePrivyWallet } from "@/hooks/usePrivyWallet";
import Cookies from "js-cookie";
import { COOKIE_NAME } from "@/constants/middleware.constants";
import { ProfileSchema } from "@/schemas/User/profile";
import type { ZodError } from "zod";
import { ConnectedAccountsSection } from "@/components/UI/ConnectedAccountsSection";
import { MergeConfirmationModal } from "@/components/UI/MergeConfirmationModal";
import { SocialAccountsOnboarding } from "@/components/UI/SocialAccountsOnboarding";

/** Mirrors `PUT /api/user/profile` body merge + `age` parsing so validation matches the API. */
function buildProfilePayloadForApi(userData: UserData, profile: ProfileData) {
	const ageRaw = parseInt(String(profile.age), 10);
	const age = Number.isNaN(ageRaw) ? 0 : ageRaw;
	return {
		...profile,
		...userData,
		age,
		gender: profile.gender === "" ? undefined : profile.gender,
	};
}

function zodIssuesToRecord(error: ZodError): Record<string, string> {
	return error.issues.reduce<Record<string, string>>((acc, issue) => {
		const key = issue.path[0];
		if (typeof key === "string") acc[key] = issue.message;
		return acc;
	}, {});
}

const Profile = memo(function Profile() {
	const {
		isConnected,
		user: currentUser,
		connect,
		publicKey,
		walletType,
		authenticated,
		privyUser,
		syncError,
		clearSyncError,
		isSyncing,
		linkEmail,
		linkEmailWithPreCheck,
		linkPhone,
		linkPhoneWithPreCheck,
		linkTwitter,
		linkGoogle,
		linkDiscord,
		linkPhantom,
		unlinkEmail,
		unlinkPhone,
		unlinkTwitter,
		unlinkGoogle,
		unlinkDiscord,
		unlinkPhantom,
		mergeConflict,
		clearMergeConflict,
		linkConflictType,
		lookupEmailForMerge,
		lookupPhoneForMerge,
		reSync,
	} = usePrivyWallet();

	const isMobile = useMemo(() => {
		if (typeof window === "undefined") return false;
		return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
	}, []);

	const hasPhantom = useMemo(() => {
		if (typeof window === "undefined") return false;
		return !!(window as any).solana?.isPhantom;
	}, []);
	// Email-based merge lookup state (used when link email/google fails with already-linked error)
	const [mergeEmailInput, setMergeEmailInput] = useState("");
	const [mergeEmailLookupState, setMergeEmailLookupState] = useState<
		"idle" | "loading" | "not_found" | "error"
	>("idle");

	// Phone-based merge lookup state (used when link phone fails — typically when a
	// mobile-first user signs in on PC with a wallet and tries to link their existing phone).
	const [mergePhoneInput, setMergePhoneInput] = useState("");
	const [mergePhoneLookupState, setMergePhoneLookupState] = useState<
		"idle" | "loading" | "not_found" | "error"
	>("idle");

	const [userData, setUserData] = useState<UserData>({
		email: "",
		phone: "",
		address: "",
		city: "",
		state: "",
		country: "",
		userName: "",
		image: "",
	});
	const [isLoading, setIsLoading] = useState(true);
	const [profile, setProfile] = useState<ProfileData>({
		age: 0,
		gender: "",
		drops: 0,
		snags: 0,
	});

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	const [selectedImage, setSelectedImage] = useState<File | null>(null);
	const [imagePreview, setImagePreview] = useState<string | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);

	// First-time onboarding modal
	const [showOnboarding, setShowOnboarding] = useState(false);

	useEffect(() => {
		if (typeof window !== "undefined" && sessionStorage.getItem("mgt_welcome") === "1") {
			setShowOnboarding(true);
		}
		// Re-check when currentUser first becomes available — handles the edge case
		// where auth completes while the user is already on this page (no navigation).
	}, [currentUser]);

	const handleOnboardingDismiss = useCallback(() => {
		sessionStorage.removeItem("mgt_welcome");
		setShowOnboarding(false);
	}, []);

	// Clear stale sync-error banners on mount and whenever the tab becomes
	// visible again. Without this, a closed/cancelled link modal could leave
	// behind a yellow banner that lingers when the user navigates back to
	// Profile (Task 9 — "weird command/text appearing after exiting linking
	// menu").
	useEffect(() => {
		clearSyncError();
		const onVisibility = () => {
			if (document.visibilityState === "visible") clearSyncError();
		};
		document.addEventListener("visibilitychange", onVisibility);
		return () => document.removeEventListener("visibilitychange", onVisibility);
	}, [clearSyncError]);

	// Memoize expensive computations
	const profileStats = useMemo(
		() => ({
			drops: profile.drops,
			snags: profile.snags,
			total: profile.drops + profile.snags,
		}),
		[profile.drops, profile.snags]
	);

	const accountInfo = useMemo(() => {
		if (!privyUser?.linkedAccounts) return [];

		return privyUser.linkedAccounts.map((account: any) => {
			switch (account.type) {
				case "email":
					return {
						label: "Email",
						value: (account as any).address || "N/A",
					};
				case "phone":
					return {
						label: "Phone",
						value: (account as any).number || "N/A",
					};
				case "wallet":
					const address =
						(account as any).address || (account as any).walletAddress;
					return {
						label: "Wallet",
						value: address
							? `${address.slice(0, 6)}...${address.slice(-4)}`
							: "N/A",
					};
				case "google_oauth":
					return {
						label: "Google",
						value: (account as any).email || "N/A",
					};
				case "twitter_oauth":
					return {
						label: "Twitter",
						value: (account as any).username || "N/A",
					};
				case "discord_oauth":
					return {
						label: "Discord",
						value: (account as any).username || "N/A",
					};
				default:
					return {
						label: account.type.charAt(0).toUpperCase() + account.type.slice(1),
						value: "Connected",
					};
			}
		});
	}, [privyUser?.linkedAccounts]);

	const fetchProfile = useCallback(async (signal?: AbortSignal) => {
		try {
			setIsLoading(true);
			message.loading({ content: "Fetching information", key: "profile" });
			const response = await apiService.get("user/profile", signal ? { signal } : undefined);

			if (response?.user) {
				const { user, profile: profileData } = response;
				setUserData({
					email: user.email || "",
					phone: user.phoneNumber || "",
					address: user.location?.address || "",
					city: user.location?.city || "",
					state: user.location?.state || "",
					country: user.location?.country || "",
					userName: user.userName || "",
					image: user.image || "",
				});

				setProfile({
					age: profileData?.age || 0,
					gender: profileData?.gender || "",
					drops: profileData?.drops || 0,
					snags: profileData?.snags || 0,
				});

				message.success({
					content: user.userName ? `Welcome, ${user.userName}` : "Profile loaded",
					key: "profile",
					duration: 2,
				});
			}
		} catch (error) {
			// Ignore requests that were intentionally aborted (component unmount / user change)
			if ((error as any)?.code === "ERR_CANCELED" || (error as any)?.name === "CanceledError") return;
			console.error(error);
			message.error({ content: "Error fetching profile data", key: "profile" });
		} finally {
			setIsLoading(false);
		}
	}, []);

	// Only fetch once the backend JWT is ready (currentUser is populated by
	// authenticateWithBackend). Keying off the id — not the object reference —
	// prevents spurious re-fetches while still re-fetching on account switches.
	const currentUserId = currentUser?.id;
	useEffect(() => {
		if (!currentUserId) return;
		const controller = new AbortController();
		fetchProfile(controller.signal);
		return () => { controller.abort(); };
	}, [currentUserId, fetchProfile]);

	// Separate cleanup effect for image preview
	useEffect(() => {
		return () => {
			if (imagePreview) {
				URL.revokeObjectURL(imagePreview);
			}
		};
	}, [imagePreview]);

	const handleInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
			const { name, value } = e.target;
			setFieldErrors((prev) => {
				if (!prev[name]) return prev;
				const next = { ...prev };
				delete next[name];
				return next;
			});
			if (name === "age") {
				if (value === "") {
					setProfile((prev) => ({ ...prev, age: 0 }));
					return;
				}
				const n = parseInt(value, 10);
				setProfile((prev) => ({
					...prev,
					age: Number.isNaN(n) ? 0 : n,
				}));
				return;
			}
			if (name in userData) {
				setUserData((prev) => ({ ...prev, [name]: value }));
			} else {
				setProfile((prev) => ({ ...prev, [name]: value }));
			}
		},
		[userData]
	);

	const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			if (file.size > 20 * 1024 * 1024) {
				message.error("File size should be less than 20MB");
				return;
			}
			if (!["image/jpeg", "image/png", "image/jpg"].includes(file.type)) {
				message.error("Only jpg, jpeg and png files are allowed");
				return;
			}

			if (imagePreview) {
				URL.revokeObjectURL(imagePreview);
			}

			setSelectedImage(file);
			const previewUrl = URL.createObjectURL(file);
			setImagePreview(previewUrl);
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		const payload = buildProfilePayloadForApi(userData, profile);
		const parsed = ProfileSchema.safeParse(payload);
		if (!parsed.success) {
			setFieldErrors(zodIssuesToRecord(parsed.error));
			message.error({
				content: "Please fix the highlighted fields before saving.",
				key: "update",
			});
			return;
		}
		setFieldErrors({});
		setIsSubmitting(true);
		try {
			message.loading({ content: "Updating profile", key: "update" });

			const formData = new FormData();
			if (selectedImage) {
				formData.append("image", selectedImage);
			}
			formData.append("userData", JSON.stringify(userData));
			formData.append(
				"profile",
				JSON.stringify({
					...profile,
					gender: profile.gender || undefined,
				})
			);

			const res = await apiService.put("user/profile", formData);

			if (res?.status) {
				message.success({
					content: "Profile updated successfully",
					key: "update",
					duration: 2,
				});
				// Clear selected image and preview
				setSelectedImage(null);
				if (imagePreview) {
					URL.revokeObjectURL(imagePreview);
					setImagePreview(null);
				}
				if (fileInputRef.current) {
					fileInputRef.current.value = "";
				}

				// Update the existing cookie with profile completion status
				const existingCookie = Cookies.get(COOKIE_NAME);
				if (existingCookie) {
					try {
						const existingUser = JSON.parse(existingCookie);
						Cookies.set(COOKIE_NAME, JSON.stringify({ ...existingUser, wheresGeoProfile: true }));
					} catch (error) {
						console.warn("Failed to update cookie:", error);
					}
				}
				fetchProfile(); // Refresh profile data
			} else {
				message.error({ content: "Error updating profile", key: "update" });
			}
		} catch (error) {
			console.error(error);
			message.error({ content: "Error updating profile", key: "update" });
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className="md:min-h-[70vh] bg-mgt-black flex items-center justify-center px-4 py-8 md:py-0">
			{/* First-time onboarding modal */}
			{showOnboarding && privyUser && (
				<SocialAccountsOnboarding
					linkedAccounts={privyUser.linkedAccounts ?? []}
					isMobile={isMobile}
					hasPhantom={hasPhantom}
					isSyncing={isSyncing}
					linkEmail={linkEmail}
					linkPhone={linkPhone}
					linkTwitter={linkTwitter}
					linkGoogle={linkGoogle}
					linkDiscord={linkDiscord}
					linkPhantom={linkPhantom}
					onDismiss={handleOnboardingDismiss}
				/>
			)}

			{!isConnected ? (
				<div className="text-center space-y-4">
					<h2 className="text-2xl font-bold text-white">
						Authentication Required
					</h2>
					<p className="text-gray-300">
						Please connect your wallet to access your profile.
					</p>
					<button
						onClick={() => connect()}
						className="bg-mgt-green text-black px-6 py-3 rounded-full font-medium hover:bg-mgt-yellow transition-colors"
					>
						Connect Wallet
					</button>
				</div>
		) : (
			<div className="w-full max-w-2xl space-y-6">
				{/* Merge conflict modal */}
				{mergeConflict && (
					<MergeConfirmationModal
						sourceProfile={mergeConflict.sourceProfile}
						targetProfile={mergeConflict.targetProfile}
						sourceUserId={mergeConflict.sourceUserId}
						targetUserId={mergeConflict.targetUserId}
						privyUserId={privyUser?.id}
					onMergeSuccess={async () => {
						clearMergeConflict();
						// Re-sync JWT cookie with the newly merged MongoDB state,
						// then reload the profile so email and other merged fields appear.
						await reSync();
						await fetchProfile();
					}}
						onCancel={clearMergeConflict}
					/>
				)}

				{/* Sync error banner */}
				{syncError && (
					<div className="bg-yellow-900/40 border border-yellow-600/40 rounded-xl px-4 py-3 space-y-2">
						<div className="flex items-start justify-between gap-3">
							<p className="text-yellow-300 text-sm">{syncError}</p>
							<button
								onClick={clearSyncError}
								className="text-yellow-400 hover:text-yellow-200 text-xs shrink-0 mt-0.5"
							>
								Dismiss
							</button>
						</div>
						{/* When an email/social link conflict is detected, offer email-based merge */}
						{linkConflictType && ["email", "google", "discord", "twitter"].includes(linkConflictType) && (
							<p className="text-yellow-400/80 text-xs">
								Enter the email of your other account below to merge them.
							</p>
						)}
					</div>
				)}

				{/* Email-based merge lookup — appears when a link attempt failed with linked_to_another_user */}
				{(linkConflictType && ["email", "google", "discord", "twitter"].includes(linkConflictType)) && (
					<div className="bg-[#1A1A1A] border border-yellow-600/30 rounded-xl px-4 py-4 space-y-3">
						<p className="text-white text-sm font-medium">Merge with your other account</p>
						<p className="text-gray-400 text-xs">
							Enter the email address of the account you tried to link. We&apos;ll find it and let you merge your drops, snags, and profile data.
						</p>
						<div className="flex gap-2">
							<input
								type="email"
								value={mergeEmailInput}
								onChange={(e) => {
									setMergeEmailInput(e.target.value);
									setMergeEmailLookupState("idle");
								}}
								placeholder="other@email.com"
								className="flex-1 bg-[#111] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-mgt-green"
							/>
							<button
								disabled={!mergeEmailInput.trim() || mergeEmailLookupState === "loading"}
								onClick={async () => {
									setMergeEmailLookupState("loading");
									const result = await lookupEmailForMerge(mergeEmailInput.trim());
									if (result === "found") {
										// mergeConflict is now set → MergeConfirmationModal will open
										setMergeEmailLookupState("idle");
										setMergeEmailInput("");
										clearSyncError();
									} else {
										setMergeEmailLookupState(result);
									}
								}}
								className="bg-mgt-green hover:bg-mgt-green/80 disabled:opacity-40 text-black text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
							>
								{mergeEmailLookupState === "loading" ? "Searching…" : "Find Account"}
							</button>
						</div>
						{mergeEmailLookupState === "not_found" && (
							<p className="text-red-400 text-xs">No account found with that email. Check the address and try again.</p>
						)}
						{mergeEmailLookupState === "error" && (
							<p className="text-red-400 text-xs">Something went wrong. Please try again.</p>
						)}
					</div>
				)}

				{/* Phone-based merge lookup — appears when a phone link attempt failed with linked_to_another_user */}
				{linkConflictType === "phone" && (
					<div className="bg-[#1A1A1A] border border-yellow-600/30 rounded-xl px-4 py-4 space-y-3">
						<p className="text-white text-sm font-medium">Merge with your other account</p>
						<p className="text-gray-400 text-xs">
							That phone number is already on another MyGeoTokens account (likely the one you created on mobile). Enter it below to find that account and merge your drops, snags, and profile data.
						</p>
						<div className="flex gap-2">
							<input
								type="tel"
								value={mergePhoneInput}
								onChange={(e) => {
									setMergePhoneInput(e.target.value);
									setMergePhoneLookupState("idle");
								}}
								placeholder="+1 555 123 4567"
								className="flex-1 bg-[#111] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-mgt-green"
							/>
							<button
								disabled={!mergePhoneInput.trim() || mergePhoneLookupState === "loading"}
								onClick={async () => {
									setMergePhoneLookupState("loading");
									const result = await lookupPhoneForMerge(mergePhoneInput.trim());
									if (result === "found") {
										setMergePhoneLookupState("idle");
										setMergePhoneInput("");
										clearSyncError();
									} else {
										setMergePhoneLookupState(result);
									}
								}}
								className="bg-mgt-green hover:bg-mgt-green/80 disabled:opacity-40 text-black text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
							>
								{mergePhoneLookupState === "loading" ? "Searching…" : "Find Account"}
							</button>
						</div>
						{mergePhoneLookupState === "not_found" && (
							<p className="text-red-400 text-xs">No account found with that phone number. Check the number and try again.</p>
						)}
						{mergePhoneLookupState === "error" && (
							<p className="text-red-400 text-xs">Something went wrong. Please try again.</p>
						)}
					</div>
				)}

				<div className="flex flex-col items-center gap-4">
						<div
							className="relative w-28 h-28 rounded-full border-2 border-mgt-green overflow-hidden cursor-pointer group"
							onClick={() => fileInputRef.current?.click()}
						>
							{!isLoading && (
								<Image
									src={imagePreview || userData.image || "/image.png"}
									alt="User Avatar"
									fill
									className="object-cover"
									priority
								/>
							)}
							<div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
								<span className="text-white text-sm">Change Photo</span>
							</div>
							<input
								type="file"
								ref={fileInputRef}
								className="hidden"
								accept="image/jpeg,image/png,image/jpg"
								onChange={handleImageChange}
							/>
						</div>

				<div className="text-center">
					<div className="flex items-center justify-center gap-2">
						{isLoading ? (
							<div className="h-8 w-36 bg-gray-700/60 rounded-md animate-pulse" />
						) : (
							<h1 className="text-2xl font-bold text-white">
								{userData.userName}
							</h1>
						)}
						{currentUser?.identityBadge === "twitter" && (
								<svg className="w-5 h-5 text-sky-400 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-label="Verified Twitter">
									<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.259 5.63 5.905-5.63zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
								</svg>
							)}
							{currentUser?.identityBadge === "google" && (
								<svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" aria-label="Verified Google">
									<path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
									<path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
									<path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
									<path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
								</svg>
							)}
							{currentUser?.identityBadge === "discord" && (
								<svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="#5865F2" aria-label="Verified Discord">
									<path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.04.036.05a19.863 19.863 0 0 0 5.993 3.03.077.077 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z" />
								</svg>
							)}
						</div>
						{isLoading ? (
							<div className="flex items-center justify-center space-x-4 mt-2">
								<div className="h-4 w-16 bg-gray-700/60 rounded-md animate-pulse" />
								<div className="w-px h-4 bg-gray-600" />
								<div className="h-4 w-16 bg-gray-700/60 rounded-md animate-pulse" />
							</div>
						) : (
							<div className="flex items-center justify-center space-x-4 mt-2">
								<div className="flex items-center space-x-1">
									<span className="text-mgt-green font-semibold">
										{profileStats.drops}
									</span>
									<span className="text-gray-400 text-sm">Drops</span>
								</div>
								<div className="w-px h-4 bg-gray-600"></div>
								<div className="flex items-center space-x-1">
									<span className="text-mgt-yellow font-semibold">
										{profileStats.snags}
									</span>
									<span className="text-gray-400 text-sm">Snags</span>
								</div>
							</div>
						)}
							{walletType && (
								<p className="text-xs text-mgt-green mt-2 flex items-center justify-center space-x-1">
									<span className="w-2 h-2 bg-green-500 rounded-full"></span>
									<span>
										{walletType.charAt(0).toUpperCase() + walletType.slice(1)}{" "}
										Wallet Connected
									</span>
								</p>
							)}
						</div>
					</div>

					<form
						onSubmit={handleSubmit}
						className="bg-[#1A1A1A] rounded-xl p-6 space-y-6"
					>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div className="space-y-4">
								<h2 className="text-lg font-semibold text-mgt-green">
									Personal Information
								</h2>
								<InputField
									label="Display Name"
									name="userName"
									type="text"
									value={userData.userName}
									onChange={handleInputChange}
									placeholder="Your display name (shown on dropped NFTs)"
									maxLength={30}
									error={fieldErrors.userName}
								/>
								<div className="space-y-1">
									<InputField
										label="Email"
										name="email"
										type="email"
										value={userData.email}
										onChange={handleInputChange}
										disabled
										error={fieldErrors.email}
									/>
									{!fieldErrors.email && (
										<p className="text-gray-400 text-xs mt-0.5">
											{userData.email
												? "To change your email, use the Connected Accounts section below."
												: "To add an email, use the Connected Accounts section below."}
										</p>
									)}
								</div>
								<InputField
									label="Phone"
									name="phone"
									type="tel"
									value={userData.phone}
									onChange={handleInputChange}
									placeholder="123-456-7890"
									error={fieldErrors.phone}
								/>
								<InputField
									label="Address"
									name="address"
									value={userData.address}
									onChange={handleInputChange}
									placeholder="123 Main St"
									error={fieldErrors.address}
								/>
							</div>

							<div className="space-y-4">
								<h2 className="text-lg font-semibold text-mgt-green">
									Location & Demographics
								</h2>
								<div className="grid grid-cols-2 gap-3">
									<InputField
										label="City"
										name="city"
										value={userData.city}
										onChange={handleInputChange}
										placeholder="New York"
										error={fieldErrors.city}
									/>
									<InputField
										label="State"
										name="state"
										value={userData.state}
										onChange={handleInputChange}
										placeholder="California"
										error={fieldErrors.state}
									/>
									<InputField
										label="Country"
										name="country"
										value={userData.country}
										onChange={handleInputChange}
										placeholder="USA"
										error={fieldErrors.country}
									/>
									<InputField
										label="Age"
										name="age"
										type="number"
										min={0}
										value={profile.age === 0 ? "" : String(profile.age)}
										onChange={handleInputChange}
										placeholder="25"
										error={fieldErrors.age}
									/>
								</div>

								<SelectField
									label="Gender"
									name="gender"
									value={profile.gender}
									onChange={handleInputChange}
									options={["Male", "Female", "Other"]}
									error={fieldErrors.gender}
								/>
							</div>
						</div>

				<button
					type="submit"
					disabled={isLoading || isSubmitting}
					className="w-full bg-mgt-green hover:bg-opacity-90 transition-colors py-3 rounded-lg font-medium text-black disabled:opacity-50"
				>
					{isSubmitting ? "Updating..." : isLoading ? "Loading..." : "Update Profile"}
				</button>
				</form>

				{/* Connected accounts with link/unlink buttons */}
				{privyUser && (
					<ConnectedAccountsSection
						linkedAccounts={privyUser.linkedAccounts ?? []}
						isMobile={isMobile}
						hasPhantom={hasPhantom}
						isSyncing={isSyncing}
						linkEmail={linkEmail}
						linkEmailWithPreCheck={linkEmailWithPreCheck}
						linkPhone={linkPhone}
						linkPhoneWithPreCheck={linkPhoneWithPreCheck}
						linkTwitter={linkTwitter}
						linkGoogle={linkGoogle}
						linkDiscord={linkDiscord}
						linkPhantom={linkPhantom}
						unlinkEmail={unlinkEmail}
						unlinkPhone={unlinkPhone}
						unlinkTwitter={unlinkTwitter}
						unlinkGoogle={unlinkGoogle}
						unlinkDiscord={unlinkDiscord}
						unlinkPhantom={unlinkPhantom}
					/>
				)}
			</div>
		)}
	</div>
	);
});

export default Profile;
